// @flow
import React, {Fragment} from 'react';
import {FormattedMessage} from 'react-intl';
import classNames from 'classnames';
import Tooltip from '@homeaway/react-tooltip';
import GeoDistance from '../GeoDistance/GeoDistance';
import Icon from '../Icon/Icon';
import Price from '../Price/Price';
import Rating from '../Rating/Rating';
import HitSuperlative from '../HitSuperlative/HitSuperlative';
import HitFreeCancellation from '../HitFreeCancellation/HitFreeCancellation';
import Details from '../Details/Details';
import {UrgencyMessage} from '@homeaway/react-urgency-messaging';

import type {Props as HitInfoProps} from './HitInfo';

type Props = {
    premierPartner: ?string,
    premierPartnerDescription: ?string,
    instantBookableTitle: string
} & HitInfoProps;

export default function HitInfoDesktop({
    amenitiesBadges,
    averageRating,
    brand,
    detailPageUrl,
    details,
    freeCancellation,
    freeCancellationVariant,
    geoDistance,
    headline,
    hideSuperlative,
    instantBookable,
    isHovered,
    numRecentViews,
    popular,
    premierPartner,
    premierPartnerDescription,
    price,
    priceSummary,
    propertyType,
    recentlyViewed,
    reviewCount,
    superlative,
    instantBookableTitle,
    isDatelessSearch,
    isMetric,
    urgencyMessage,
    useUrgencyMessage,
    locale
}: Props) {
    let tooltipParentNode;
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
        tooltipParentNode = document.getElementsByTagName('body')[0];
    }

    return (
        <div className="HitInfo HitInfo--desktop">
            <div className="HitInfo__content">
                {useUrgencyMessage && (
                    <UrgencyMessage
                        className="HitInfo__content--urgency"
                        size="s"
                        unitMessage={{iconTextV1: urgencyMessage}}
                    />
                )}
                {!useUrgencyMessage &&
                    recentlyViewed && (
                        <div
                            className="HitInfo__viewedUrgency hidden-xs"
                            data-wdio="viewed-urgency-message"
                        >
                            <small>
                                {popular && (
                                    <div className="HitInfo__popular label label--urgency">
                                        <FormattedMessage
                                            defaultMessage="Popular"
                                            description="Popular property based on number of recent views"
                                            id="viewedUrgencyPopular"
                                        >
                                            {(txt) => (
                                                <Fragment>{txt}</Fragment>
                                            )}
                                        </FormattedMessage>
                                    </div>
                                )}
                                {numRecentViews &&
                                    numRecentViews >= 5 && (
                                        <FormattedMessage
                                            id="viewedUrgencyLabel"
                                            description="viewed times in the last 48 hours"
                                            defaultMessage="Viewed {numRecentlyViewed} times in the last 48 hours"
                                            values={{
                                                numRecentlyViewed: numRecentViews
                                            }}
                                        >
                                            {(txt) => (
                                                <Fragment>{txt}</Fragment>
                                            )}
                                        </FormattedMessage>
                                    )}
                            </small>
                        </div>
                    )}
                <h4
                    className="HitInfo__headline hover-text hidden-xs"
                    href={detailPageUrl}
                >
                    {headline}
                </h4>
                {geoDistance && (
                    <div className="HitInfo__distance hidden-xs">
                        <GeoDistance
                            brand={brand}
                            geoDistance={geoDistance}
                            isHighlighted={isHovered}
                        />
                    </div>
                )}
                <div className="HitInfo__details">
                    <Details
                        amenitiesBadges={amenitiesBadges}
                        details={details}
                        isMetric={isMetric}
                        propertyType={propertyType}
                    />
                </div>
            </div>
            <div className="HitInfo__infoBar hover-bg">
                <div className="HitInfo__priceSuperlativeGroup">
                    {/* SERP 979 CONTROL - just show superlative */}
                    {freeCancellationVariant === 0 &&
                        (!!superlative && (
                            <span className="HitInfo__superlative small">
                                <HitSuperlative {...superlative} />
                            </span>
                        ))}
                    {/* SERP 979 VARIANT 1 - Only show cancellation , or VARIANT 3 - Show cancellation here, and move rating*/}
                    {(freeCancellationVariant === 1 ||
                        freeCancellationVariant === 3) &&
                        (!!freeCancellation && (
                            <span className="HitInfo__cancellation small">
                                <HitFreeCancellation />
                            </span>
                        ))}
                    {/* SERP 979 VARIANT 2 - Show cancellation if it exists, otherwise show superlative */}
                    {freeCancellationVariant === 2 &&
                        ((!!freeCancellation && (
                            <span className="HitInfo__cancellation small">
                                <HitFreeCancellation />
                            </span>
                        )) ||
                            (!!superlative && (
                                <span className="HitInfo__superlative small">
                                    <HitSuperlative {...superlative} />
                                </span>
                            )))}
                    <div className="HitInfo__price">
                        {instantBookable && (
                            <Icon
                                name="instant-book"
                                iconClass="InstantBook__icon"
                                title={instantBookableTitle}
                            />
                        )}
                        <Price
                            price={price}
                            priceSummary={priceSummary}
                            useAverage={isDatelessSearch}
                            locale={locale}
                        />
                    </div>
                </div>
                {/* SERP 979 - Need to render slightly different enclosing div's in V3 */}
                {freeCancellationVariant === 3 && (
                    <div
                        className={classNames({
                            HitInfo__superlativeBadgeRatingGroup: premierPartner,
                            HitInfo__superlativeRatingGroup: !premierPartner
                        })}
                    >
                        <div className="HitInfo__badgeRatingGroup">
                            {premierPartner && (
                                <Tooltip
                                    tooltipType="popover"
                                    content={premierPartnerDescription}
                                    placement="bottom"
                                    parentNode={tooltipParentNode}
                                >
                                    <div
                                        className="HitInfo__premierBadge bg-brand small"
                                        data-wdio="premier-partner-badge"
                                    >
                                        {premierPartner}
                                    </div>
                                </Tooltip>
                            )}
                            {reviewCount > 0 && (
                                <div className="HitInfo__rating">
                                    <Rating
                                        averageRating={averageRating}
                                        reviewCount={reviewCount}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="HitInfo__lowerSuperlativeGroup">
                            {/* SERP 979 - Show the superlative next to the rating if we're in variant 3 */}
                            {!hideSuperlative &&
                                (!!superlative && (
                                    <div className="HitInfo__priceSuperlativeGroup--lower">
                                        <span className="HitInfo__superlative small">
                                            <HitSuperlative {...superlative} />
                                        </span>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
                {freeCancellationVariant !== 3 && (
                    <div className="HitInfo__badgeRatingGroup">
                        {premierPartner && (
                            <Tooltip
                                tooltipType="popover"
                                content={premierPartnerDescription}
                                placement="bottom"
                                parentNode={tooltipParentNode}
                            >
                                <div
                                    className="HitInfo__premierBadge bg-brand small"
                                    data-wdio="premier-partner-badge"
                                >
                                    {premierPartner}
                                </div>
                            </Tooltip>
                        )}
                        {reviewCount > 0 && (
                            <div className="HitInfo__rating">
                                <Rating
                                    averageRating={averageRating}
                                    reviewCount={reviewCount}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
